import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core';

import { combineLatest as observableCombineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

import type { RtButtonVariant } from '@core/user-interface-legacy';
import { BulkSelectComponent, CheckboxComponent } from '@core/user-interface-legacy';

import { BulkActionsController } from '../bulk-actions.controller';
import { BulkActionsSelectionTracker } from '../bulk-actions.selection-tracker';

@Component({
  selector: 'rt-bulk-actions-batch-select',
  templateUrl: './bulk-actions-batch-select.component.html',
  styleUrls: ['./bulk-actions-batch-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.select]': 'type === "select"',
    '[class.checkbox]': 'type === "checkbox"',
    '[class.is-disabled]': 'disabled',
  },
  imports: [BulkSelectComponent, CheckboxComponent],
})
export class BulkActionsBatchSelectComponent {
  private readonly bulkCtrl = inject(BulkActionsController);
  private readonly tracker = inject(BulkActionsSelectionTracker);

  protected readonly cdRef = inject(ChangeDetectorRef);

  @Input() public type: 'select' | 'checkbox' = 'select';
  @Input() public variant: Extract<RtButtonVariant, 'tertiary' | 'outline'> = 'tertiary';

  public selected = false;
  public disabled = false;
  public active: boolean;
  public currentViewSelectedCount = 0;
  public currentViewTotalCount = 0;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.active = this.bulkCtrl.active;
  }

  public ngOnInit(): void {
    this.bulkCtrl.active$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(active => {
      this.active = active;
      this.cdRef.detectChanges();
    });

    observableCombineLatest(this.tracker.state$, this.tracker.items$)
      .pipe(
        map(
          ([state, items]) => state.currentViewSelectedCount === items.length && items.length > 0,
        ),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(selected => {
        this.selected = selected;
        this.cdRef.detectChanges();
      });

    this.tracker.items$
      .pipe(
        map(items => items.length === 0),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(disabled => {
        this.disabled = disabled;
        this.cdRef.detectChanges();
      });

    this.tracker.state$
      .pipe(distinctUntilChanged(), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(state => {
        this.currentViewSelectedCount = state.currentViewSelectedCount;
        this.currentViewTotalCount = state.currentViewTotalCount;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isIndeterminateState(): boolean {
    return (
      this.currentViewSelectedCount > 0 &&
      this.currentViewSelectedCount < this.currentViewTotalCount
    );
  }

  public handleSelect(toggle: boolean): void {
    this.tracker.items$
      .pipe(take(1), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(items => {
        this.bulkCtrl.selectItems(items || [], toggle);
      });
  }
}
